/**
 * Order-flow line panes under the main chart (lightweight-charts v5 panes:
 * `chart.addSeries(LineSeries, opts, paneIndex)`), time-synced with the
 * candles for free.
 *
 * Pane layout, top to bottom under the price pane (index 0), only for the
 * groups that have a line on:
 *   volume:    market_volume_imbalance + stop_volume, zero line, each on its
 *              own scale but both symmetric around zero so the zero line
 *              holds for both
 *   book:      order_book_imbalance
 *   liquidity: average_liquidity + price_dynamics (own scales)
 * The first line added to a pane gets the pane's right axis, a partner an
 * overlay scale; when the axis owner goes, the partner is re-created on it. Removing a pane's last series removes the pane
 * (v5 drops empty panes; removePane covers a pane that stayed).
 *
 * Points snap to the candles: each one lands on the bar that contains it and
 * the last value per bar wins. Points outside every bar (before the oldest
 * loaded candle, in a gap, past the newest bar) are dropped, so the lines
 * never add time-scale slots that would shift the candles' logical indices.
 */
import {
  LineSeries,
  LineStyle,
  type AutoscaleInfo,
  type IChartApi,
  type IPaneApi,
  type ISeriesApi,
  type LineData,
  type MouseEventParams,
  type Time,
} from 'lightweight-charts';
import { FLOW_LINES, type FlowLineGroup, type FlowLineName, type FlowLinePoint } from '../../store/flowLinesStore';

/** Pane order under the price pane. */
export const FLOW_LINE_GROUPS: readonly FlowLineGroup[] = ['volume', 'book', 'liquidity'];

/**
 * Stretch factor of each line pane against the price pane's 1: one pane
 * takes 1/6 of the chart, all three together 3/8, so the price stays dominant.
 */
export const FLOW_LINE_PANE_STRETCH = 0.2;

const ZERO_LINE_COLOR = 'rgba(148, 163, 184, 0.55)';
const SCALE_MARGINS = { top: 0.12, bottom: 0.12 };
/**
 * The volume pane is symmetric around zero, so its extreme value (and the
 * series' "MVI 277.1" badge) sits half the usable height away from the axis's
 * "0.0" tick. In a ~60px pane the 12% margins left that at ~23px, under two
 * label heights, and the badge touched the tick; thin margins give it room.
 */
export const VOLUME_SCALE_MARGINS = { top: 0.04, bottom: 0.04 };
/** Axis label height (badge or tick), px, incl. its padding. */
export const AXIS_LABEL_PX = 20;

/** Price-scale margins for a group's pane. */
export function flowLineScaleMargins(group: FlowLineGroup): { top: number; bottom: number } {
  return group === 'volume' ? VOLUME_SCALE_MARGINS : SCALE_MARGINS;
}

/**
 * Centre-to-centre px between the "0.0" tick and a value badge at the edge of a
 * zero-symmetric pane of this height.
 */
export function zeroLabelClearance(paneHeightPx: number, margins: { top: number; bottom: number }): number {
  return (paneHeightPx * (1 - margins.top - margins.bottom)) / 2;
}

const CONFIG = new Map(FLOW_LINES.map((l) => [l.name, l]));

// ------------------------------------------------------------- snapping

/** Index of the last bar starting at or before `sec`, or -1. */
function barIndexAt(barTimes: ArrayLike<number>, sec: number): number {
  let lo = 0;
  let hi = barTimes.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (barTimes[mid] <= sec) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/**
 * Bar time (unix seconds) of the candle containing `tsMs`, or null when it is
 * in no bar: before the first, in a gap, or past the newest bar's end.
 * `barTimes` ascending bar starts; a bar ends at the next bar's start or
 * `stepSec` after its own, whichever is first.
 */
export function snapToBar(tsMs: number, barTimes: ArrayLike<number>, stepSec: number): number | null {
  const sec = tsMs / 1000;
  const i = barIndexAt(barTimes, sec);
  if (i < 0) return null;
  const start = barTimes[i];
  const end = i + 1 < barTimes.length ? Math.min(barTimes[i + 1], start + stepSec) : start + stepSec;
  return sec < end ? start : null;
}

/** Line data for a series: points (ascending) on bars, last value per bar. */
export function snapLinePoints(
  points: readonly FlowLinePoint[],
  barTimes: ArrayLike<number>,
  stepSec: number,
): LineData<Time>[] {
  const out: LineData<Time>[] = [];
  if (barTimes.length === 0) return out;
  for (const [ts, value] of points) {
    const t = snapToBar(ts, barTimes, stepSec);
    if (t === null) continue;
    const last = out[out.length - 1];
    if (last && (last.time as number) === t) last.value = value;
    else if (!last || (last.time as number) < t) out.push({ time: t as Time, value });
  }
  return out;
}

/**
 * Points of `next` added after `prev`'s newest point, or null when `next` is
 * not `prev` (minus a capped head) plus a tail: e.g. history merged in under
 * points already held, which needs a full re-set.
 */
export function appendedPoints(
  prev: readonly FlowLinePoint[] | undefined,
  next: readonly FlowLinePoint[] | undefined,
): readonly FlowLinePoint[] | null {
  if (!next || next === prev) return [];
  if (!prev || prev.length === 0) return null;
  const tail = prev[prev.length - 1];
  // Merges copy point references, so the old newest point is found by identity, near the end.
  const i = next.lastIndexOf(tail);
  if (i < 0) return null;
  // Everything up to the old tail must be prev's own run: nothing inserted before it.
  const head = prev.length - 1 - i;
  if (head < 0 || next[0] !== prev[head]) return null;
  return next.slice(i + 1);
}

/** Something drawn at a price on the price pane (order, position, alert...). */
export interface PriceOverlayItem {
  key: string;
  label: string;
  price: number;
  color: string;
}

/**
 * Items whose price sits at or below the bottom of the price pane (`top` of
 * the line panes), highest price first: their line is clipped, so the chart
 * shows them as badges on the pane edge instead.
 */
export function belowPricePane(
  items: readonly PriceOverlayItem[],
  toY: (price: number) => number | null,
  top: number,
): PriceOverlayItem[] {
  return items
    .filter((it) => {
      const y = toY(it.price);
      return y !== null && y >= top;
    })
    .sort((a, b) => b.price - a.price);
}

/** Autoscale that keeps zero in the middle. */
const symmetricAroundZero = (base: () => AutoscaleInfo | null): AutoscaleInfo | null => {
  const r = base();
  if (!r?.priceRange) return r;
  const m = Math.max(Math.abs(r.priceRange.minValue), Math.abs(r.priceRange.maxValue)) || 1;
  return { ...r, priceRange: { minValue: -m, maxValue: m } };
};

// ------------------------------------------------------------- pointer events

/**
 * Container-relative vertical span [top, bottom) of the line panes, or null
 * when the chart has none. Pane 0 is the price pane; each further pane sits
 * below a 1px separator.
 */
export function linePaneSpan(chart: IChartApi): { top: number; bottom: number } | null {
  const panes = chart.panes();
  if (panes.length <= 1) return null;
  const top = panes[0].getHeight();
  let bottom = top;
  for (let i = 1; i < panes.length; i += 1) bottom += 1 + panes[i].getHeight();
  return { top, bottom };
}

/**
 * Did this lightweight-charts event happen in a line pane? Its `point` is then
 * relative to that pane, so price-pane hit tests must ignore it.
 */
export function isLinePaneEvent(chart: IChartApi | null, container: HTMLElement | null, param: MouseEventParams): boolean {
  if (!chart || chart.panes().length <= 1) return false;
  if (param.paneIndex !== undefined) return param.paneIndex > 0;
  const clientY = param.sourceEvent?.clientY;
  if (clientY === undefined || !container) return false;
  const span = linePaneSpan(chart);
  if (!span) return false;
  const y = clientY - container.getBoundingClientRect().top;
  return y >= span.top && y < span.bottom;
}

// ------------------------------------------------------------- panes

interface LineEntry {
  series: ISeriesApi<'Line'>;
  scaleId: string;
  lastTime: number;
}

/** The flow line series and panes on one chart. */
export class FlowLinePanes {
  private readonly lines = new Map<FlowLineName, LineEntry>();
  private readonly panes = new Map<FlowLineGroup, IPaneApi<Time>>();

  constructor(private readonly chart: IChartApi) {}

  /** Names with a series, in pane order. */
  names(): FlowLineName[] {
    return FLOW_LINES.map((l) => l.name).filter((n) => this.lines.has(n));
  }

  paneCount(): number {
    return this.panes.size;
  }

  /** Show exactly `names` (colours by name); adds and removes series and panes. */
  sync(names: readonly FlowLineName[], colors: Partial<Record<FlowLineName, string>>): void {
    const want = new Set(names.filter((n) => CONFIG.has(n)));
    for (const name of [...this.lines.keys()]) if (!want.has(name)) this.remove(name);
    for (const group of FLOW_LINE_GROUPS) {
      // The axis owner left: re-create the partner on the axis (its data is
      // re-set by the caller, which re-feeds every line when the set changes).
      const present = FLOW_LINES.filter((l) => l.group === group && this.lines.has(l.name));
      if (present.length > 0 && !present.some((l) => this.lines.get(l.name)!.scaleId === 'right')) {
        for (const l of present) this.remove(l.name);
      }
      for (const l of FLOW_LINES) {
        if (l.group !== group || !want.has(l.name)) continue;
        const entry = this.lines.get(l.name);
        if (entry) entry.series.applyOptions({ color: colors[l.name] ?? '#94a3b8' });
        else this.add(l.name, colors[l.name] ?? '#94a3b8');
      }
    }
  }

  setPoints(name: FlowLineName, data: LineData<Time>[]): void {
    const entry = this.lines.get(name);
    if (!entry) return;
    entry.series.setData(data);
    entry.lastTime = data.length > 0 ? (data[data.length - 1].time as number) : -Infinity;
  }

  /** Live point; false when it is older than the series' last bar (caller re-sets the data). */
  update(name: FlowLineName, point: LineData<Time>): boolean {
    const entry = this.lines.get(name);
    if (!entry) return true;
    const t = point.time as number;
    if (t < entry.lastTime) return false;
    entry.series.update(point);
    entry.lastTime = t;
    return true;
  }

  destroy(): void {
    for (const name of [...this.lines.keys()]) this.remove(name);
  }

  private add(name: FlowLineName, color: string): void {
    const cfg = CONFIG.get(name)!;
    let pane = this.panes.get(cfg.group);
    let paneIndex: number;
    if (pane) {
      paneIndex = pane.paneIndex();
    } else {
      // Below the price pane and every earlier group that is showing.
      paneIndex = 1 + FLOW_LINE_GROUPS.slice(0, FLOW_LINE_GROUPS.indexOf(cfg.group)).filter((g) => this.panes.has(g)).length;
      if (paneIndex < this.chart.panes().length) {
        // A later group holds that slot: insert a pane there.
        this.chart.addPane().moveTo(paneIndex);
      }
    }
    // The pane's first line gets its right axis, a partner an overlay scale.
    const axisTaken = FLOW_LINES.some((l) => l.group === cfg.group && this.lines.get(l.name)?.scaleId === 'right');
    const scaleId = axisTaken ? `flow-${name}` : 'right';
    const series = this.chart.addSeries(LineSeries, {
      color,
      lineWidth: 1,
      priceScaleId: scaleId,
      priceLineVisible: false,
      lastValueVisible: true,
      title: cfg.short,
      priceFormat: { type: 'price', precision: 1, minMove: 0.1 },
      ...(cfg.group === 'volume' ? { autoscaleInfoProvider: symmetricAroundZero } : {}),
    }, paneIndex);
    // A new pane's right scale starts from the price pane's options, and the
    // chart pins that one (autoScale off, fixed range): these must autoscale.
    series.priceScale().applyOptions({ autoScale: true, scaleMargins: flowLineScaleMargins(cfg.group) });
    if (cfg.group === 'volume') {
      series.createPriceLine({
        price: 0, color: ZERO_LINE_COLOR, lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '',
      });
    }
    if (!pane) {
      pane = series.getPane();
      pane.setStretchFactor(FLOW_LINE_PANE_STRETCH);
      this.panes.set(cfg.group, pane);
    }
    this.lines.set(name, { series, scaleId, lastTime: -Infinity });
  }

  private remove(name: FlowLineName): void {
    const entry = this.lines.get(name);
    if (!entry) return;
    this.lines.delete(name);
    const group = CONFIG.get(name)!.group;
    try {
      this.chart.removeSeries(entry.series);
    } catch {
      /* chart already removed */
    }
    if (FLOW_LINES.some((l) => l.group === group && this.lines.has(l.name))) return;
    const pane = this.panes.get(group);
    this.panes.delete(group);
    // v5 drops a pane with its last series; remove one that stayed anyway.
    try {
      const idx = pane?.paneIndex() ?? -1;
      const panes = this.chart.panes();
      if (idx > 0 && idx < panes.length && panes[idx].getSeries().length === 0) this.chart.removePane(idx);
    } catch {
      /* chart already removed */
    }
  }
}
